import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Evidence } from '@/api/types/readiness'
import { Toast } from '@/components/readiness/EvidenceArchiveUi'

// ── Evidence archive: undo toast and deferred write (Iteration 5 §13, Note 6) ─
// `Archive` collapses the row and shows a toast with Undo and a 2px countdown
// draining over 8 s; hover or keyboard focus pauses it (WCAG 2.2.1). The
// archive is held in client state for the window and written on expiry, on
// `visibilitychange`, `pagehide` and route change (unmount); an undo within the
// window writes nothing. Up to three toasts stack; beyond that they merge into
// "{n} items archived · Undo all".

import { UNDO_MS } from '@/components/readiness/EvidenceArchiveUi'
const TICK_MS = 50
const MAX_TOASTS = 3

interface Pending {
  id: string
  title: string
  remaining: number
}

export function useArchiveQueue({ onCommit, onUndo }: { onCommit: (id: string) => void; onUndo: (id: string) => void }) {
  const [pending, setPending] = useState<Pending[]>([])
  const [paused, setPaused] = useState(false)
  const pendingRef = useRef<Pending[]>([])
  pendingRef.current = pending
  const commitRef = useRef(onCommit)
  commitRef.current = onCommit

  const flushAll = useCallback(() => {
    const ids = pendingRef.current.map((p) => p.id)
    if (!ids.length) return
    pendingRef.current = []
    setPending([])
    ids.forEach((id) => commitRef.current(id))
  }, [])

  // Countdown: one interval while anything is pending; paused on hover / focus.
  useEffect(() => {
    if (!pending.length || paused) return
    const t = window.setInterval(() => {
      const next = pendingRef.current.map((p) => ({ ...p, remaining: p.remaining - TICK_MS }))
      const expired = next.filter((p) => p.remaining <= 0)
      pendingRef.current = next.filter((p) => p.remaining > 0)
      setPending(pendingRef.current)
      // The toast is gone before the write lands.
      expired.forEach((p) => commitRef.current(p.id))
    }, TICK_MS)
    return () => window.clearInterval(t)
  }, [pending.length, paused])

  // Flush when the page is hidden, unloaded or left (route change = unmount).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') flushAll() }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', flushAll)
    return () => {
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('pagehide', flushAll)
      flushAll()
    }
  }, [flushAll])

  const archive = useCallback((e: Evidence) => setPending((ps) => (ps.some((p) => p.id === e.id) ? ps : [...ps, { id: e.id, title: e.title, remaining: UNDO_MS }])), [])
  // Undo removes the toast under the pointer / focus, so the pause it caused
  // ends with it.
  const undo = useCallback((id: string) => {
    setPending((ps) => ps.filter((p) => p.id !== id))
    setPaused(false)
    onUndo(id)
  }, [onUndo])
  const undoAll = useCallback(() => {
    const ids = pendingRef.current.map((p) => p.id)
    setPending([])
    setPaused(false)
    ids.forEach((id) => onUndo(id))
  }, [onUndo])
  useEffect(() => { if (!pending.length) setPaused(false) }, [pending.length])
  const pendingIds = new Set(pending.map((p) => p.id))

  const toasts = pending.length
    ? createPortal(
        <div className="rd rd-toast-stack" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setPaused(false) }} data-testid="archive-toasts">
          {pending.length > MAX_TOASTS ? (
            <Toast title={`${pending.length} items archived`} remaining={Math.min(...pending.map((p) => p.remaining))} onUndo={undoAll} undoLabel="Undo all" testId="archive-toast-merged" />
          ) : (
            pending.map((p) => <Toast key={p.id} title={`Evidence archived · ${p.title}`} remaining={p.remaining} onUndo={() => undo(p.id)} testId={`archive-toast-${p.id}`} />)
          )}
        </div>,
        document.body
      )
    : null

  return { archive, undo, isPending: (id: string) => pendingIds.has(id), toasts, flushAll }
}

